<template>
  <div v-show="!loading" class="ad-header2">
    <!-- Hero Banner -->
    <div class="ad-hero2">
      <div class="ad-hero2-content">
        <span class="ad-badge" :class="item.is_compulsory ? 'ad-badge--danger' : 'ad-badge--secondary'">
          {{ item.is_compulsory ? $t("assignment.compulsory") : $t("assignment.not_compulsory") }}
        </span>
        <h1 class="ad-title2">{{ item.name }}</h1>

        <!-- Meta bilgileri -->
        <div class="ad-meta2-row">
          <div v-if="item.education_total_duration > 0" class="ad-meta-item">
            <i class="icon-feather-clock"></i>
            <span>{{ item.education_total_duration }} {{ $t("general.minute") }}</span>
          </div>
          <div class="ad-meta2-item">
            <i class="icon-feather-users"></i>
            <span>{{ item.assigned_users_count }} {{ $t("education.Person_Assigned_Training") }}</span>
          </div>
          <div class="ad-meta2-item">
            <i class="icon-feather-check-circle"></i>
            <span>{{ item.completed_users_count }} {{ $t("education.Person_Completed_Training") }}</span>
          </div>
          <div v-if="assignmentInstructors.length" class="ad-meta2-item ad-meta2-instructors">
            <i class="uil-graduation-hat"></i>
            <span>{{ $t("general.instructors") }}:</span>
            <span class="ad-instructor-list">
              <span
                v-for="(instructor, index) in assignmentInstructors"
                :key="instructor.id || `assignment-instructor-${index}`"
                class="ad-instructor-chip"
              >
                {{ instructor.name }}
              </span>
            </span>
          </div>
        </div>

        <!-- Tarih bilgileri -->
        <div class="ad-date-row">
          <div class="ad-date-item">
            <i class="icon-feather-flag"></i>
            <span>{{ $t("education.Start_date") }}: <strong>{{ formattedStartDate }}</strong></span>
          </div>
          <div v-if="item.provider === null" class="ad-date-item">
            <i class="icon-feather-calendar"></i>
            <span>{{ $t("education.End_date") }}: <strong>{{ formattedEndDate }}</strong></span>
          </div>
        </div>

        <!-- Süre doldu uyarısı -->
        <div v-if="item.is_expired" class="ad-alert ad-alert--warning">
          <i class="icon-feather-alert-triangle"></i>
          <span>{{ $t("general.time_finished") }}</span>
        </div>

        <!-- Yetkinlikler -->
        <div v-if="item.competencies && item.competencies.length > 0" class="competencies-hover">
          <span class="competencies-trigger">
            <i class="uil-award"></i>
            {{ $t('general.competencies') }}
            <span class="competencies-count">({{ item.competencies.length }})</span>
          </span>
          <div class="competencies-popup">
            <div class="popup-header">
              <i class="uil-award"></i>
              {{ $t('general.competencies') }}
            </div>
            <div class="popup-content">
              <div v-for="competency in item.competencies" :key="competency.id" class="competency-item">
                <span class="competency-name">{{ competency.name }}</span>
                <span v-if="competency.requires_approval" class="approval-required">
                  <i class="uil-check-circle"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Rating -->
        <div class="ad-rating">
          <div class="star-rating m-0 p-0 d-flex align-items-center">
            <span class="ad-rating-value">{{ item.total_rating }}</span>
            <star-rating :increment="0.1" :max-rating="5" :padding="1" :rating="item.total_rating" :read-only="true"
              :show-rating="false" :star-points="STAR_POINTS" :star-size="18" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarRating from "vue-star-rating";
import { convertUTCToLocal } from "@/utils/timeUtils";
import moment from "moment";

const STAR_POINTS = [23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17];

export default {
  name: "AssignmentDescription",
  components: { StarRating },
  props: {
    item: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { STAR_POINTS };
  },
  computed: {
    assignmentInstructors() {
      if (!Array.isArray(this.item?.instructors)) return [];

      return this.item.instructors
        .map((instructor) => {
          const user = instructor?.user || {};
          const name =
            [user.name, user.surname].filter(Boolean).join(" ").trim() ||
            instructor?.full_name ||
            instructor?.name ||
            "";

          return {
            id: instructor?.id,
            name,
          };
        })
        .filter((instructor) => instructor.name);
    },
    formattedStartDate() {
      const localDate = convertUTCToLocal(this.item.start_date);
      if (!localDate) return '-';
      return moment(localDate, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm');
    },
    formattedEndDate() {
      const localDate = convertUTCToLocal(this.item.end_date);
      if (!localDate) return '-';
      return moment(localDate, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm');
    },
  },
};
</script>
<style>
.ad-header2 {
  margin-bottom: 14px;
}

.ad-hero2 {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  border: 1px solid #e2e8f0;
}

.ad-hero2-content {
  max-width: 100%;
}

.ad-title2 {
  font-size: 24px;
  font-weight: 700;
  margin: 12px 0 20px;
  line-height: 1.3;
  color: #1e293b;
}

.ad-meta2-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
}

.ad-meta2-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #475569;
}

.ad-meta2-instructors {
  flex-wrap: wrap;
}

.ad-instructor-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 5px;
}

.ad-instructor-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
</style>
